import { StemType, TICKS_PER_LOOP } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, useEffect, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { DAW_COLORS, styled } from '../../../lib/stitches'
import ProfileRegistry from '../../../stores/registry/ProfileRegistry'
import { IClipStore } from '../../../stores/song/clip/ClipStore'
import { ILoopRefStore } from '../../../stores/song/loop/LoopRefStore'
import { INoteCollectionStore } from '../../../stores/song/loop/NoteCollectionStore'
import { ISongStore } from '../../../stores/song/SongStore'
import { IAudioStemStore } from '../../../stores/song/stem/AudioStemStore'
import { IStemStore } from '../../../stores/song/StemStore'
import { IStudioStore } from '../../../stores/StudioStore'
import { capitalizeFirstLetter } from '../../../utils/strings'
import { Box, Row } from '../../core/Components'
import { StemIcon } from '../../core/icons'
import Text from '../../core/Text/Text'
import { defaultProfPic } from '../../core/Utils'
import withTooltip from '../../core/withTooltip'
import { MINTABLE_COLORS_MAP } from '../../platform/color'
import { useSampleTimelineControls } from '../../player/sample/SampleTimelineControls'
import useCanvasVisualizer from '../../studio/loops/useCanvasVisualizer'
import { getStemDisplayName } from '../../studio/StudioUtils'
import AudioVisualizer from '../AudioVisualizer'

const TRACK_MARGIN = '4px'
const TRACK_HEIGHT = '26px'
const TRACK_HEADER_WIDTH = '95px'
const BORDER_RADIUS = '4px'
const LOOP_MARGIN_PX = 1
const LOOP_MARGIN = `${LOOP_MARGIN_PX}px`
const LOOP_WIDTH_PX = 64
const LOOP_WIDTH = `${LOOP_WIDTH_PX}px`
const LOOP_PADDING = '2px'

const TrackContainer = styled('div', {
  height: TRACK_HEIGHT,
  display: 'flex',
  borderRadius: BORDER_RADIUS,
  alignItems: 'stretch',
  background: 'rgba(0,0,0,.05)',
  margin: TRACK_MARGIN
})

const TrackHeader = styled('div', {
  width: TRACK_HEADER_WIDTH,
  padding: '0px 4px',
  boxSizing: 'border-box',
  borderRadius: BORDER_RADIUS,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '$gray01',
  boxShadow: '3px 0px 8px 0 rgba(0,0,0, 0.1)'
})

const LoopGroupWrapper = styled('div', {
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'stretch',
  margin: LOOP_MARGIN,
  borderRadius: BORDER_RADIUS
})

const LoopGroup = styled('div', {
  display: 'flex',
  alignItems: 'stretch'
})

const Loop = styled('div', {
  margin: LOOP_MARGIN,
  padding: LOOP_PADDING,
  borderRadius: BORDER_RADIUS,
  width: LOOP_WIDTH,
  boxSizing: 'border-box'
})

const ClipGroup = styled('div', {
  display: 'flex',
  position: 'relative',
  maxheight: TRACK_HEIGHT
})

const Clip = styled('div', {
  display: 'flex',
  padding: '4px 2px',
  boxSizing: 'border-box',
  borderRadius: BORDER_RADIUS,
  position: 'absolute',
  top: LOOP_MARGIN,
  bottom: LOOP_MARGIN
})

interface StemVisualizerProps {
  stem: IStemStore
  length: number
  progress: number
  color?: string
}

interface LoopVisualizerProps {
  section: number
  stem: IStemStore
  color?: string
}

interface ClipVisualizerProps {
  clip: IClipStore
  songLength16thNotes: number
  color?: string
}

const Canvas = styled('canvas', { width: '100%', height: '100%' })

const LoopCanvasVisualizer: FC<{ loop: ILoopRefStore; isDrums: boolean; noteColor: string }> =
  observer(({ loop, isDrums, noteColor }) => {
    const notes: INoteCollectionStore = isDrums
      ? loop.drumLoop.condensedNotesForVisualization
      : loop.melodicLoop.notes
    const ref = useCanvasVisualizer(notes, false, undefined, noteColor)
    return <Canvas ref={ref} />
  })

const LoopVisualizer: FC<LoopVisualizerProps> = observer(({ section, stem, color }) => {
  const loop: ILoopRefStore | null = stem.getLoopAtSectionOrNull(section)
  const isDrums = stem.type() === StemType.DRUMS
  const backgroundColor = color && (MINTABLE_COLORS_MAP[color]?.LIGHT || `${color}46`)
  const background = loop ? backgroundColor : undefined
  return (
    <Loop css={{ minWidth: LOOP_WIDTH, background }}>
      {loop && <LoopCanvasVisualizer loop={loop} isDrums={isDrums} noteColor={color} />}
    </Loop>
  )
})

const ClipAudioVisualizer: FC<{ clip: IClipStore; wavColor: string }> = observer(
  ({ clip, wavColor }) => {
    const [audioBuffer, setAudioBuffer] = useState<AudioBuffer>(null)

    useEffect(() => {
      clip.sampleWithEffects.getSampleBuffer().then((ab) => setAudioBuffer(ab))
    }, [clip?.sampleWithEffects?.sampleIndex])

    if (!audioBuffer) return null
    return (
      <AudioVisualizer
        audioBuffer={audioBuffer}
        options={{ color: wavColor, segmentWidth: 1, segmentSpacing: 0.8, resolution: 2 }}
      />
    )
  }
)

const ClipVisualizer: FC<ClipVisualizerProps> = observer(({ clip, songLength16thNotes, color }) => {
  const backgroundColor = color && (MINTABLE_COLORS_MAP[color]?.LIGHT || `${color}46`)
  const background = clip ? backgroundColor : undefined
  const songLengthTicks = (TICKS_PER_LOOP * songLength16thNotes) / 64
  const left = `${100 * (clip.startInComposition48n / songLengthTicks)}%`
  const width = `${
    100 * ((clip.endInComposition48n - clip.startInComposition48n) / songLengthTicks)
  }%`
  return (
    <Clip css={{ left, width, background }}>
      {clip && <ClipAudioVisualizer clip={clip} wavColor={color} />}
    </Clip>
  )
})

const Avatar = withTooltip(
  styled('img', {
    display: 'block',
    width: '18px',
    height: '18px',
    backgroundColor: '$background__tertiary',
    border: '1px solid white',
    borderRadius: '9999px'
  })
)

const StemVisualizer: FC<StemVisualizerProps> = observer(
  ({ stem, length, progress, color = DAW_COLORS.indigo05 }) => {
    const [sample, setSample] = useState<IArpMedia>(null)

    // To calculate how much to translate non-audio loops, we need to take into account the margin between them
    const loopTranslate = ((LOOP_WIDTH_PX + LOOP_MARGIN_PX * 2) / LOOP_WIDTH_PX) * progress

    useEffect(() => {
      ArpRegistry.getByIdAsync(stem.getCurrentSample().sampleIndex).then((arp) => setSample(arp))
    }, [stem.getCurrentSample().sampleIndex])

    const displayName = getStemDisplayName(stem)
    const filled = stem.isRemixed && color !== undefined && color !== null
    const fillColor = color && (MINTABLE_COLORS_MAP[color]?.MEDIUM || color)
    const backgroundColor = color && `${color}0A`
    const sampleAuthor = sample && ProfileRegistry.getByIndexReactive(sample.user.walletAddress)
    const stemItem = stem.remixedStemId > -1 && ArpRegistry.retrieveFromCache(stem.remixedStemId)
    const stemAuthor = stemItem && ProfileRegistry.getByIndexReactive(stemItem.user.walletAddress)
    return (
      <TrackContainer>
        <TrackHeader
          css={{
            background: filled ? fillColor : undefined,
            color: filled ? '$text_inverted__primary' : '$text__primary'
          }}
        >
          <Text
            size={'10'}
            css={{
              fontWeight: '$medium',
              color: 'currentColor',
              overflow: 'hidden',
              display: 'flex',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              alignItems: 'center',
              marginRight: '4px'
            }}
          >
            {filled && (
              <StemIcon
                style={{
                  minWidth: '9px',
                  minHeight: '9px',
                  width: '9px',
                  height: '9px',
                  marginRight: '4px'
                }}
              />
            )}
            {capitalizeFirstLetter(displayName)}
          </Text>
          <Row css={{ alignItems: 'center' }}>
            {stemAuthor && (
              <a
                style={{ all: 'unset', display: 'block', transform: 'translateX(50%)', zIndex: 6 }}
                href={`/profile/${stemAuthor.wallet_address}/created/songs`}
                target="_blank"
                rel="noreferrer"
              >
                <Avatar
                  key={stemAuthor.wallet_address}
                  src={stemAuthor.profile_picture || defaultProfPic()}
                  tooltipText={`Stem Author - ${stemAuthor.display_name || 'Unknown'}`}
                />
              </a>
            )}
            {sampleAuthor && (
              <a
                style={{ all: 'unset', display: 'block', transform: stemAuthor && 'scale(0.8)' }}
                href={`/profile/${sampleAuthor.wallet_address}/created/songs`}
                target="_blank"
                rel="noreferrer"
              >
                <Avatar
                  key={sampleAuthor.wallet_address}
                  src={sampleAuthor.profile_picture || defaultProfPic()}
                  tooltipText={`Sample Author - ${sampleAuthor.display_name || 'Unknown'}`}
                />
              </a>
            )}
          </Row>
        </TrackHeader>
        <LoopGroupWrapper css={{ backgroundColor }}>
          {stem.type() === StemType.AUDIO && (
            <ClipGroup
              css={{
                minWidth: `${(length / 64) * LOOP_WIDTH_PX}px`,
                transform: `translateX(-${progress}%)`
              }}
            >
              {(stem as IAudioStemStore).clips.map((clip) => (
                <ClipVisualizer
                  clip={clip}
                  color={color}
                  songLength16thNotes={length}
                  key={clip.uniqueId}
                />
              ))}
            </ClipGroup>
          )}
          {stem.type() !== StemType.AUDIO && (
            <LoopGroup
              css={{
                width: `${(length / 64) * LOOP_WIDTH_PX}px`,
                transform: `translateX(-${loopTranslate}%)`
              }}
            >
              {Array.from(Array(Math.floor(length)).keys()).map((item, section) => (
                <LoopVisualizer key={section} section={section} stem={stem} color={color} />
              ))}
            </LoopGroup>
          )}
        </LoopGroupWrapper>
      </TrackContainer>
    )
  }
)

interface SongVisualizerProps {
  song: ISongStore
  color?: string
  player: HTMLAudioElement | IStudioStore
}

const VisualizerContainer = styled('div', {
  borderRadius: BORDER_RADIUS,
  overflowY: 'scroll',
  '&::-webkit-scrollbar': { display: 'none' },
  '-ms-overflow-style': 'none',
  margin: `-${TRACK_MARGIN}`,
  scrollbarWidth: 'none'
})

const SongVisualizer: FC<SongVisualizerProps & ComponentProps<typeof VisualizerContainer>> =
  observer(({ song, color = DAW_COLORS.indigo04, player, ...props }) => {
    // Pretty messy, but player can be IStudioStore or HTMLAudioElement for the
    // case when we have a Data URI for the song vs when we don't,
    // so here we check to see which one it is and calculate the progress based
    // on that -- we keep it in this function so as to minimize which elements are rerendered
    const isAudioPlayer: boolean = player instanceof HTMLAudioElement
    let { duration, currentTime } = useSampleTimelineControls(
      isAudioPlayer ? (player as HTMLAudioElement) : null
    )
    if (!isAudioPlayer) {
      duration = (player as IStudioStore).song.songMintLength16Notes
      currentTime = (player as IStudioStore).playhead
    }
    const progress = (currentTime / duration) * 100

    const stems = song.stems
    const length = song.songMintLength16Notes
    return (
      <VisualizerContainer {...props}>
        <Box css={{ background: '$gray02' }}>
          {stems.map((e) => (
            <StemVisualizer
              key={e.uniqueId}
              stem={e}
              color={color}
              length={length}
              progress={progress}
            />
          ))}
        </Box>
      </VisualizerContainer>
    )
  })

export default SongVisualizer
